import { useMemo } from "react";
import type { LocalMessage } from "stream-chat";
import {
  MESSAGE_ACTIONS,
  MessageProvider,
  MessageSimple,
  useChannelStateContext,
  useMessageContext,
} from "stream-chat-react";
import { isCustomAttachment } from "Components/Chat/chatAttachmentTypes";
import { useChatFindHighlight } from "Components/Chat/ChatFindHighlightContext";
import { useChatMessageReport } from "Components/Chat/ChatMessageReportContext";
import { useDisclosedNameResolver } from "Hooks/Chat/useDisclosedNameResolver";
import { Resources, useResource } from "Translations/Resources";

/**
 * The message UI with two viewer-side corrections applied to the message context, the vendor
 * MessageSimple itself reused untouched:
 *
 * - The author's name is resolved for THIS viewer (ADR-0007). stream-chat-react renders it straight
 *   from `message.user.name` into a bare span with no smaller seam to override.
 * - A payment message (payment request / direct payment attachment) cannot be edited, only deleted:
 *   its text is a caption on a transaction that already exists, and the edit form would let the
 *   attachment be dropped or the caption rewritten under a settled payment.
 * - That caption ("Payment request · 100 Kč", built by buildPaymentMessageText at send time) is
 *   hidden in the bubble: it exists so push banners and previews have something to show, while the
 *   card already renders the amount and status.
 * - Another user's message gets a "Report" action (DSA notice, ZS-1743). Injected per message
 *   rather than through MessageList's `customMessageActions`, which would put it on the viewer's
 *   own messages too; the SDK renders the action's key as its label, so the key is the translation.
 *
 * Do not reimplement MessageSimple here: everything else about the message (attachments, reactions,
 * options) must keep coming from the library.
 */
export const ChatMessageWithDisclosedName = () => {
  const messageContext = useMessageContext();
  const { channel } = useChannelStateContext();
  const resolveName = useDisclosedNameResolver();
  const { t } = useResource();

  const { message } = messageContext;
  const streamName = message.user?.name;
  const userId = message.user?.id;

  // A frozen channel is anonymised for both sides (ADR-0008), authors included — otherwise every
  // message in the history would name the person the channel header deliberately hides.
  const isAnonymized = channel.data?.frozen === true;
  const anonymousName = t(Resources.Common.AnonymousUser);

  const isPaymentMessage =
    message.attachments?.some(isCustomAttachment) ?? false;

  // "Find in conversation" keeps its current match highlighted for as long as the bar is open;
  // the SDK's own flash (jumpToMessage) is OR-ed in, not replaced.
  const { highlightedMessageId: findHighlightedMessageId } =
    useChatFindHighlight();
  const isFindHighlighted = findHighlightedMessageId === message.id;

  const reportMessage = useChatMessageReport();
  const reportLabel = t(Resources.Social.PublicProfile.Report);
  const isReportable =
    !!reportMessage && !messageContext.isMyMessage() && !message.deleted_at;

  const adjustedContext = useMemo(() => {
    const disclosedName = isAnonymized
      ? anonymousName
      : resolveName(userId, streamName);
    const isNameAdjusted = !!message.user && disclosedName !== streamName;

    if (
      !isNameAdjusted &&
      !isPaymentMessage &&
      !isFindHighlighted &&
      !isReportable
    ) {
      return messageContext;
    }

    return {
      ...messageContext,
      highlighted: messageContext.highlighted || isFindHighlighted,
      ...(isReportable && reportMessage
        ? {
            customMessageActions: {
              ...messageContext.customMessageActions,
              [reportLabel]: (reportedMessage: LocalMessage) =>
                reportMessage(reportedMessage),
            },
          }
        : {}),
      message: {
        ...message,
        ...(isNameAdjusted && message.user
          ? { user: { ...message.user, name: disclosedName } }
          : {}),
        // The caption exists for push banners and channel previews (buildPaymentMessageText);
        // the card already shows everything, so the bubble renders it alone.
        ...(isPaymentMessage ? { text: "" } : {}),
      },
      getMessageActions: isPaymentMessage
        ? () =>
            messageContext
              .getMessageActions()
              .filter((action) => action !== MESSAGE_ACTIONS.edit)
        : messageContext.getMessageActions,
    };
    // The message OBJECT identity changes on every render of a re-rendered list, so the memo keys
    // on the fields actually read — otherwise this rebuilds the context needlessly.
  }, [
    messageContext,
    message,
    userId,
    streamName,
    resolveName,
    isAnonymized,
    anonymousName,
    isPaymentMessage,
    isFindHighlighted,
    isReportable,
    reportMessage,
    reportLabel,
  ]);

  return (
    <MessageProvider value={adjustedContext}>
      <MessageSimple />
    </MessageProvider>
  );
};
